import { formatNumber } from '@trmediaab/zebra-utils';
import classnames from 'classnames';
import { GameType } from 'live/components/index.types';
import PropTypes from 'prop-types';
import React from 'react';

import logoAtg from 'images/games/atg.png';
import logoStryktipset from 'images/games/sport.png';
import Button from 'shared/components/button/Button';
import CloseButton from 'shared/components/button/CloseButton';
import Star from 'shared/components/icon/Star';
import { formatEpoch } from 'shared/utils/dateUtils';
import { getProductDisplayName } from 'shared/utils/productUtils';
import { getText } from 'shared/utils/Text';

import styles from './index.module.scss';

const SelectedGamePopup = ({
  error,
  game,
  isAdmin,
  loading,
  userId,

  onDelete,
  onClose,
}) => {
  if (!game) {
    return null;
  }

  const product = game.atgProduct || game.sportProduct;
  const productType = game.atgProduct ? 'atg' : 'sport';
  const urlPart = game.atgProduct ? 't' : 's';

  const handleDelete = () => {
    if (window.confirm(getText('page-live-game-delete-prompt'))) {
      onDelete({ id: game.id, type: game.type });
    }
  };

  const timeLeft = game.closeTime - Date.now();
  const showDeleteBtn =
    game.betCode && (isAdmin || (game.uid === userId && timeLeft > 0));

  return (
    <div
      className={styles.wrapper}
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.text}>
            {getText('page-live-system-header')}
          </div>
          <div className={styles.buttonCloseContainer}>
            <CloseButton
              border={false}
              className={styles.buttonClose}
              large
              onClick={onClose}
            />
          </div>
        </div>

        <div className={styles.list}>
          <div className={styles.item}>
            <div className={styles.name}>
              {getText('page-live-filter-header-name')}
            </div>
            <div className={styles.value}>{game.alias || game.name}</div>
          </div>

          {product && (
            <div className={styles.item}>
              <div className={styles.name}>
                {getText('page-live-filter-header-game')}
              </div>
              <div className={styles.value}>
                <img
                  alt={getProductDisplayName(product)}
                  className={styles.gameIcon}
                  src={productType === 'atg' ? logoAtg : logoStryktipset}
                />
                <span>{getProductDisplayName(product)}</span>
              </div>
            </div>
          )}

          {(game.resultsCompleteAt || game.closeTime) && (
            <div className={styles.item}>
              <div className={styles.name}>
                {getText('page-live-filter-header-date')}
              </div>
              <div className={classnames(styles.value, styles.date)}>
                {formatEpoch(
                  game.resultsCompleteAt || game.closeTime,
                  'dddd D MMM',
                )}
              </div>
            </div>
          )}

          <div className={styles.item}>
            <div className={styles.name}>
              {getText('page-live-filter-header-stake')}
            </div>
            <div className={styles.value}>
              {formatNumber(game.stake || game.dividend, {
                maximumFractionDigits: 0,
              })}
              &nbsp;
              {getText('currency-short')}
            </div>
          </div>

          {game.completedEvents > 0 && (
            <div className={styles.item}>
              <div className={styles.name}>
                {getText('page-live-filter-header-events-completed')}
              </div>
              <div className={styles.value}>
                {game.events === game.completedEvents && game.errors === 0 && (
                  <Star className={styles.star} />
                )}

                {game.events > game.completedEvents && !game.eventStarted && (
                  <div className={classnames(styles.dot, styles.disabled)} />
                )}

                <b>
                  {game.completedEvents - game.errors}&shy;
                  <span className={styles.total}>
                    /
                    {productType === 'sport'
                      ? game.events
                      : game.completedEvents}
                  </span>
                </b>
              </div>
            </div>
          )}
        </div>

        {error && <div className={styles.alert}>{error}</div>}

        {game.betCode && (
          <a
            className={styles.button}
            href={`/${urlPart}/${game.betCode}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {getText('page-live-system-button-joker')}
          </a>
        )}

        {showDeleteBtn && (
          <Button
            className={styles.button}
            disabled={loading}
            onClick={handleDelete}
          >
            {getText('page-live-system-button-delete')}
          </Button>
        )}

        <Button
          className={classnames(styles.button, styles.close)}
          disabled={loading}
          onClick={onClose}
        >
          {getText('page-live-system-button-close')}
        </Button>
      </div>
    </div>
  );
};

SelectedGamePopup.propTypes = {
  game: GameType,
  isAdmin: PropTypes.bool,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  userId: PropTypes.number,
};

export default SelectedGamePopup;
